import { useState, useMemo } from 'react';

import { PBXDirectoryEntry } from '../../../../types';
import TCClient from '../../../../client';
import { downloadFile } from '../../../../common/utils';
import styles from '../OrganizationSettings.module.css';
import { ReactComponent as DownloadButtonSvg } from './../../../../common/images/downloadPBX.svg';
import { ReactComponent as UploadButtonSvg } from './../../../../common/images/uploadPBX.svg';

type PBXDirectoryProps = {
  organizationId: string;
  searchTerm: string;
  openModal: (name: string, options?: unknown) => void;
  directoryEntries: PBXDirectoryEntry[];
  isLoading: boolean;
  getPbxDirectory: () => Promise<void>;
};

export default function PBXDirectory({
  organizationId,
  searchTerm,
  openModal,
  directoryEntries,
  isLoading,
  getPbxDirectory,
}: PBXDirectoryProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  const filteredEntries = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const filtered = term
      ? directoryEntries.filter(
          (entry) =>
            entry.extensionNumber.toLowerCase().includes(term) ||
            entry.displayName.toLowerCase().includes(term)
        )
      : directoryEntries;

    return [...filtered].sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [directoryEntries, searchTerm]);

  const downloadDirectoryTemplate = () => {
    const csvContent = 'extensionNumber,displayName,phoneNumber,location,department,title\n';
    downloadFile('pbx_directory_template.csv', 'text/csv', csvContent);
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleDelete = async () => {
    if (selectedIds.size === 0 || isDeleting) return;
    setIsDeleting(true);
    try {
      const ids = Array.from(selectedIds).map(Number);
      await TCClient.pbx.batchDeletePbxDirectoryEntries({ orgToken: organizationId, ids });
      setSelectedIds(new Set());
      await getPbxDirectory();
    } catch (err) {
      console.error('Failed to delete PBX directory entries', err);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div>
      <div className={styles.outerFlexContainer}>
        <div className={styles.numbersHeader}>
          <div>PBX Directory</div>
        </div>
        <div className={styles.bulkUpload}>
          {selectedIds.size > 0 && (
            <div className={styles.fileUpload}>
              <button
                className={styles.uploadButton}
                style={{ width: 'auto', marginRight: 20 }}
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : `Delete (${selectedIds.size})`}
              </button>
            </div>
          )}
          <div className={styles.fileUpload}>
            <button
              className={styles.directoryCreateButton}
              onClick={() =>
                openModal('createOrEditPBXDirectoryEntry', { onSuccess: getPbxDirectory })
              }
            >
              + Create
            </button>
          </div>
          <div className={styles.fileUpload}>
            <button className={styles.uploadButton} onClick={() => openModal('uploadPBXDirectory')}>
              <div className={styles.buttonSvg}>
                <UploadButtonSvg />
              </div>
              Click to upload
            </button>
          </div>
          <div className={styles.fileUpload}>
            <button
              className={styles.downloadButton}
              onClick={(e) => {
                e.preventDefault();
                downloadDirectoryTemplate();
              }}
            >
              <div className={styles.buttonSvg}>
                <DownloadButtonSvg />
              </div>
              <span className={styles.templateText}>Template</span>
            </button>
          </div>
        </div>
      </div>
      <div className={styles.directoryHeaderRow}>
        <span className={styles.checkbox} />
        <span className={styles.directoryExtension}>Extension</span>
        <span className={styles.directoryColumn}>Display Name</span>
        <span className={styles.directoryColumn}>Phone Number</span>
        <span className={styles.directoryColumn}>Location</span>
        <span className={styles.directoryColumn}>Department</span>
        <span className={styles.directoryColumn}>Title</span>
        <span className={styles.directoryEditSpacer} />
      </div>
      {isLoading && <div className={styles.row}>Loading...</div>}
      {!isLoading && filteredEntries.length === 0 && (
        <div className={styles.row}>No results found</div>
      )}
      {!isLoading &&
        filteredEntries.map((entry) => (
          <div
            className={styles.row}
            key={entry.extensionNumber}
            data-test-id={`directory-entry-${entry.extensionNumber}`}
          >
            {entry.id && (
              <span className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={selectedIds.has(entry.id)}
                  onChange={() => toggleSelect(entry.id as string)}
                />
              </span>
            )}
            <span className={styles.directoryExtension}>{entry.extensionNumber}</span>
            <span className={styles.directoryColumn}>{entry.displayName}</span>
            <span className={styles.directoryColumn}>{entry.phoneNumber}</span>
            <span className={styles.directoryColumn}>{entry.location}</span>
            <span className={styles.directoryColumn}>{entry.department}</span>
            <span className={styles.directoryColumn}>{entry.title}</span>
            <span
              className={styles.editIcon}
              role="button"
              tabIndex={0}
              onClick={() =>
                openModal('createOrEditPBXDirectoryEntry', {
                  entry,
                  onSuccess: getPbxDirectory,
                })
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  openModal('createOrEditPBXDirectoryEntry', {
                    entry,
                    onSuccess: getPbxDirectory,
                  });
                }
              }}
            />
          </div>
        ))}
    </div>
  );
}
